if (!customElements.get('hero-carousel')) {
  class HeroCarousel extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.timer = null;
      this.touchStartX = null;
      this.touchCurrentX = null;
      this.pausedByInteraction = false;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onKeyDown = this.onKeyDown.bind(this);
      this.onMouseEnter = () => this.setInteractionPause(true);
      this.onMouseLeave = () => this.setInteractionPause(false);
      this.onFocusIn = () => this.setInteractionPause(true);
      this.onFocusOut = this.handleFocusOut.bind(this);
      this.onTouchStart = this.handleTouchStart.bind(this);
      this.onTouchMove = this.handleTouchMove.bind(this);
      this.onTouchEnd = this.handleTouchEnd.bind(this);
      this.onMotionChange = () => this.restartAutoplay();
      this.onEditorBlockSelect = this.handleEditorBlockSelect.bind(this);
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide-index]'));
      this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
      this.previousButton = this.querySelector('[data-carousel-previous]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.status = this.querySelector('[data-carousel-status]');
      this.autoplay = this.dataset.autoplay === 'true';
      this.interval = Math.max(Number(this.dataset.interval) || 6000, 3000);

      this.previousButton?.addEventListener('click', () => this.showPrevious());
      this.nextButton?.addEventListener('click', () => this.showNext());
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goToSlide(Number(dot.dataset.carouselDot)));
      });

      this.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchmove', this.onTouchMove, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd);
      this.prefersReducedMotion.addEventListener?.('change', this.onMotionChange);
      document.addEventListener('shopify:block:select', this.onEditorBlockSelect);

      this.updateSlides();
      this.restartAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.removeEventListener('keydown', this.onKeyDown);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchmove', this.onTouchMove);
      this.removeEventListener('touchend', this.onTouchEnd);
      this.prefersReducedMotion.removeEventListener?.('change', this.onMotionChange);
      document.removeEventListener('shopify:block:select', this.onEditorBlockSelect);
    }

    goToSlide(index) {
      if (!this.slides?.length) return;
      this.activeIndex = (index + this.slides.length) % this.slides.length;
      this.updateSlides();
      this.restartAutoplay();
    }

    showPrevious() { this.goToSlide(this.activeIndex - 1); }
    showNext() { this.goToSlide(this.activeIndex + 1); }

    updateSlides() {
      this.slides.forEach((slide, index) => {
        const active = index === this.activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('[data-slide-focusable]').forEach((element) => {
          element.tabIndex = active ? 0 : -1;
        });
      });

      this.dots?.forEach((dot, index) => {
        const active = index === this.activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (this.status) {
        this.status.textContent = `Slide ${this.activeIndex + 1} of ${this.slides.length}`;
      }
    }

    restartAutoplay() {
      this.stopAutoplay();
      if (!this.autoplay || this.pausedByInteraction || this.prefersReducedMotion.matches || this.slides.length <= 1) return;
      this.timer = window.setInterval(() => this.showNext(), this.interval);
    }

    stopAutoplay() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    setInteractionPause(paused) {
      this.pausedByInteraction = paused;
      this.restartAutoplay();
    }

    handleFocusOut(event) {
      if (!this.contains(event.relatedTarget)) this.setInteractionPause(false);
    }

    onKeyDown(event) {
      if (this.slides.length <= 1) return;
      const actions = {
        ArrowLeft: () => this.showPrevious(),
        ArrowRight: () => this.showNext(),
        Home: () => this.goToSlide(0),
        End: () => this.goToSlide(this.slides.length - 1),
      };
      if (actions[event.key]) {
        event.preventDefault();
        actions[event.key]();
      }
    }

    handleTouchStart(event) {
      this.touchStartX = event.changedTouches[0]?.clientX ?? null;
      this.touchCurrentX = null;
    }

    handleTouchMove(event) {
      this.touchCurrentX = event.changedTouches[0]?.clientX ?? null;
    }

    handleTouchEnd() {
      if (this.touchStartX === null || this.touchCurrentX === null) return;
      const distance = this.touchStartX - this.touchCurrentX;
      if (Math.abs(distance) >= 45) distance > 0 ? this.showNext() : this.showPrevious();
      this.touchStartX = null;
      this.touchCurrentX = null;
    }

    handleEditorBlockSelect(event) {
      const slide = event.target.closest?.('[data-slide-index]');
      if (!slide || !this.contains(slide)) return;
      this.setInteractionPause(true);
      this.goToSlide(Number(slide.dataset.slideIndex));
    }
  }

  customElements.define('hero-carousel', HeroCarousel);
}